import React, { forwardRef } from 'react';
import { cn } from '../../lib/utils';
import { X } from 'lucide-react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hint?: string;
  error?: string;
  icon?: React.ReactNode;
  iconTrailing?: React.ReactNode;
  inputSize?: 'sm' | 'md' | 'lg';
  clearable?: boolean;
  onClear?: () => void;
}

const sizeStyles: Record<string, string> = {
  sm: 'px-3 py-2 text-xs rounded-xl',
  md: 'px-4 py-2.5 text-sm rounded-2xl',
  lg: 'px-4.5 py-3 text-base rounded-2xl',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, icon, iconTrailing, inputSize = 'md', clearable, onClear, className, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-content">
            {label}
            {props.required && <span className="text-danger-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-content-muted">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'w-full rounded-md border bg-surface text-content placeholder:text-content-muted',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              'transition-all duration-200 backdrop-blur-xl bg-surface-raised/84',
              error ? 'border-danger-500 focus:ring-danger-500' : 'border-border',
              icon && 'pl-9',
              (iconTrailing || clearable) && 'pr-9',
              sizeStyles[inputSize],
              className
            )}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            {...props}
          />
          {(iconTrailing || (clearable && props.value)) && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {clearable && props.value ? (
                <button type="button" onClick={onClear} className="text-content-muted hover:text-content transition-colors">
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <span className="text-content-muted">{iconTrailing}</span>
              )}
            </div>
          )}
        </div>
        {error && <p id={`${inputId}-error`} className="text-xs text-danger-500">{error}</p>}
        {hint && !error && <p id={`${inputId}-hint`} className="text-xs text-content-muted">{hint}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
